import type { Product } from '@/types/product';

/**
 * Ticket parent/child classification (backend `Product.ticketCategory`):
 *   - 'main'       → parent: a park-entry ticket, sold standalone.
 *   - 'attraction' → child: attached to a main ticket as an add-on (per visitor).
 * The BE rejects a cart with no main ticket, so attractions are never sold alone.
 */
export const isMainTicket = (p: Product): boolean =>
  (p.ticketCategory ?? '').toLowerCase() === 'main';

export const isAttraction = (p: Product): boolean =>
  (p.ticketCategory ?? '').toLowerCase() === 'attraction';

/**
 * A product an operator may actually put on sale. INACTIVE products are taken
 * off sale in the back office and must never render in a POS catalog or add-on
 * pool. The `GET /product` list endpoint returns every status (it is shared with
 * the admin management view, which needs to see inactive rows), so this filter
 * is the POS's own safeguard — the add-on flows already apply the same check.
 */
export const isSellable = (p: Product): boolean => p.status !== 'INACTIVE';

/**
 * Free-of-charge ticket (Rp 0, ticketbox-only). FOC tickets never appear in
 * the regular catalog; they are sold from the FOC page with a supporting
 * document, and a cart never mixes the two.
 */
export const isFoc = (p: Product): boolean => p.isFoc === true;

/** Which flow a cart belongs to — regular sale, or free of charge. */
export type CartMode = 'regular' | 'foc';

/** Regular catalog (SellPage): sellable entry tickets, FOC ones excluded. */
export const isRegularCatalogProduct = (p: Product): boolean =>
  isMainTicket(p) && isSellable(p) && !isFoc(p);

/** FOC catalog (FocSellPage): sellable FOC entry tickets only. */
export const isFocCatalogProduct = (p: Product): boolean =>
  isMainTicket(p) && isSellable(p) && isFoc(p);

/**
 * Attraction add-on pool for the Details step. A regular cart never sees FOC
 * attractions; an FOC cart is offered FOC attractions only, so an FOC sale is
 * always free (the server refuses a paid add-on on it, FOC_CART_INVALID).
 */
export const isAttractionForMode = (p: Product, mode: CartMode): boolean =>
  isAttraction(p) && isSellable(p) && (mode === 'foc' ? isFoc(p) : !isFoc(p));
